import React, { useState, useEffect } from 'react';
import { Grid, Button, Select, MenuItem, FormLabel, FormControl, Box, Input, Icon, ListItemIcon, ListItemText } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import FilterIcon from '@mui/icons-material/FilterList';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, size } from 'global/lib/lodash';
import setFilter from 'redux/actions/setFilter';
import { TaskFilter, FILTERDEFAULTS } from 'global/utils/filter';
import { TaskStatus } from 'global/lib/filter';
import { useScreen } from 'global/styles';
import { FilterRow, FlexFilters, FilterBtnGroup, filterFormGridSx, filterFormItemSx, filterFormLabelSx, filterIconSx, filterFormControlPaddingSx } from 'global/styles/filters';
import DiagContainer from 'global/components/diag-container';
import { setBodyOverflow } from 'global/hooks/useBodyOverflow';
import { OwnershipTask, TaskContent } from 'global/lib/filter';
import { sortAlphaNum, cleanCopy } from 'global/utils';
import { FilterChips } from 'global/components/filterchips';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useFilter } from '../../../global/hooks/useFilter';
import { ensureArray } from '../../../global/utils';
import { useTradeTypes } from '../../../global/components/trade-select';
import { useTaskTypes } from '../task-type-utils/useTaskTypes';
import { getAvailableTaskTypes, getTaskTypeIcon } from '../task-type-utils';

export default function PerformTasksFilters({ display, vendors }) {
  const [showFilters, setShowFilters] = useState(false);
  const filter = useSelector((state) => get(state, `filter.tasks.${display}`, TaskFilter()));
  const dispatch = useDispatch();
  const fhook = useFilter();
  const { ownership, status, content, scorecard_id, trade_types, task_type_ids } = filter;

  const prefixToUse = 'vendor';

  const { isMobile } = useScreen();
  const useModalFilters = isMobile;
  const filtersAreMobile = useModalFilters || isMobile;

  const showContentToggle = display === 'scorecard';
  const isSC = display === 'scorecard';

  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      useCache: { reload: 20, store: 'data.scorecards' },
      params: {
        include: 'realms',
      },
    }
  );

  const trades = useTradeTypes();
  const { data: taskTypesData } = useTaskTypes();
  const availableTaskTypes = getAvailableTaskTypes(taskTypesData);

  useEffect(() => {
    setShowFilters(!useModalFilters);
  }, [useModalFilters]);

  const changeFilter = (filt) => {
    dispatch(setFilter('tasks', display, filt));
    setBodyOverflow();
  };

  const handleStatusChange = (e) => {
    changeFilter(set({ ...filter }, `status`, e.target.value));
  };

  const handleScorecardChange = (e) => {
    const hasAny = e.target.value.find((v) => v === '0') || !size(e.target.value);
    changeFilter(set({ ...filter }, `scorecard_id`, hasAny ? [0] : e.target.value.filter((f) => !!f)));
  };

  const handleTradeChange = (e) => {
    const hasAny = e.target.value.find((v) => v === '0') || !size(e.target.value);
    changeFilter(set({ ...filter }, `trade_types`, hasAny ? [0] : e.target.value.filter((f) => !!f)));
  };

  const handleTaskTypeChange = (e) => {
    const hasAny = e.target.value.find((v) => v === '0') || !size(e.target.value);
    changeFilter(set({ ...filter }, `task_type_ids`, hasAny ? [0] : e.target.value.filter((f) => !!f)));
  };

  const handleOwnership = (e, val) => {
    if (val !== null) {
      changeFilter(set({ ...filter }, `ownership`, val));
    }
  };

  const handleContent = (e, val) => {
    if (val !== null) {
      changeFilter(set({ ...filter }, `content`, val));
    }
  };

  const handleVendorChange = (e) => {
    const vend = get(vendors, 'data', []).find((v) => v.id == e.target.value);
    changeFilter(set({ ...filter }, `${prefixToUse}_id`, get(vend, `${prefixToUse}Id`, null)));
  };

  function toggleFilters() {
    setShowFilters(!showFilters);
  }

  function clearFilters() {
    changeFilter(cleanCopy(get(FILTERDEFAULTS, `tasks.${display}`, TaskFilter())));
  }

  const hasVendors = size(get(vendors, 'data')) > 0;
  const client = get(vendors, 'data', []).find((v) => get(v, [prefixToUse, 'id']) == get(filter, `${prefixToUse}_id`));
  const scorecards_data = fhook.filterScorecards(scorecards.data);

  return (
    <Box sx={{
      displayPrint: "none"
    }}>
      {useModalFilters && (
        <FilterRow isMobile={filtersAreMobile}>
          <Button aria-label="filters" size="small" onClick={toggleFilters}>
            <FilterIcon fontSize="inherit" sx={filterIconSx} /> {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
          <FilterChips id={`tasks.${display}`} />
        </FilterRow>
      )}
      <DiagContainer useDialog={useModalFilters} title="Filters" onClose={toggleFilters} onClear={clearFilters} open={showFilters}>
        <form autoComplete="off" margin="normal">
          <Grid container sx={{
            alignContent: "center"
          }}>
            <Grid item xs={12} sx={filterFormGridSx}>
              <FlexFilters isMobile={filtersAreMobile}>
                <FilterBtnGroup exclusive size="small" value={ownership} onChange={handleOwnership}>
                  {OwnershipTask.map((t) => (
                    <ToggleButton key={t.value} value={t.value}>
                      {t.title}
                    </ToggleButton>
                  ))}
                </FilterBtnGroup>

                {/* <ToggleButtonGroup className={classes.btnGroup} size="small" value={realm_type} onChange={handleRealmType}>
                  <ToggleButton title="Lot" value='lot'><HomeIcon /></ToggleButton>
                  <ToggleButton title="Community" value='community'><CommunityIcon /></ToggleButton>
                  <ToggleButton title="Division" value='division'><DivisionIcon /></ToggleButton>
                </ToggleButtonGroup> */}

                {showContentToggle && (
                  <Box sx={{ mt: 1 }}>
                    <FilterBtnGroup exclusive size="small" value={content} onChange={handleContent}>
                      {TaskContent.map((t) => (
                        <ToggleButton key={t.value} value={t.value}>
                          {t.title}
                        </ToggleButton>
                      ))}
                    </FilterBtnGroup>
                  </Box>
                )}
              </FlexFilters>
            </Grid>

            <Grid item xs={12} sx={filterFormGridSx}>
              <FormControl variant="standard" sx={[filterFormItemSx, filterFormControlPaddingSx]}>
                <FormLabel htmlFor="Standard-scorecard-simple" sx={filterFormLabelSx}>
                  Status
                </FormLabel>
                <Select value={status} onChange={handleStatusChange}>
                  {TaskStatus.map((ts, i) => (
                    <MenuItem key={i} value={ts.value}>
                      {ts.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isSC && (
              <Grid item xs={12} sx={filterFormGridSx}>
                <FormControl variant="standard" sx={[filterFormItemSx, filterFormControlPaddingSx]}>
                  <FormLabel htmlFor="Standard-scorecard-simple" sx={filterFormLabelSx}>
                    Trade Type
                  </FormLabel>
                  <Select multiple value={ensureArray(trade_types)} onChange={handleTradeChange}>
                    <MenuItem value={'0'}>
                      <em>Any</em>
                    </MenuItem>
                    {trades.data.map((ts, i) => (
                      <MenuItem key={i} value={ts.id}>
                        {ts._name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {isSC && size(availableTaskTypes) > 0 && (
              <Grid item xs={12} sx={filterFormGridSx} data-testid="tasks-filter-task-types-section">
                <FormControl variant="standard" sx={[filterFormItemSx, filterFormControlPaddingSx]}>
                  <FormLabel sx={filterFormLabelSx}>
                    Task Types
                  </FormLabel>
                  <Select
                    data-testid="tasks-filter-task-types"
                    multiple
                    value={ensureArray(task_type_ids)}
                    onChange={handleTaskTypeChange}
                    renderValue={(selected) => {
                      if (!selected.length || (selected.length === 1 && selected[0] == '0')) return 'Any';
                      return selected.map((id) => {
                        const tt = availableTaskTypes.find((t) => String(t.id) === id);
                        return tt ? tt.name : id;
                      }).join(', ');
                    }}
                  >
                    <MenuItem value={'0'}>
                      <em>Any</em>
                    </MenuItem>
                    {availableTaskTypes.map((tt, i) => (
                      <MenuItem key={tt.id} value={String(tt.id)} data-testid={`tasks-filter-task-type-option-${tt.id}`}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Icon fontSize="small">{getTaskTypeIcon(tt)}</Icon>
                        </ListItemIcon>
                        <ListItemText>{tt.name}</ListItemText>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {isSC && (
              <Grid item xs={12} sx={filterFormGridSx}>
                <FormControl variant="standard" sx={[filterFormItemSx, filterFormControlPaddingSx]}>
                  <FormLabel htmlFor="Standard-scorecard-simple" sx={filterFormLabelSx}>
                    Scorecard
                  </FormLabel>
                  <Select multiple value={ensureArray(scorecard_id)} onChange={handleScorecardChange} input={<Input name="scorecard" id="Standard-scorecard-simple" />}>
                    <MenuItem value={'0'}>
                      <em>Any</em>
                    </MenuItem>
                    <MenuItem value={'none'}>
                      <em>None</em>
                    </MenuItem>
                    {size(scorecards_data) &&
                      scorecards_data.map((card, i) => {
                        return (
                          <MenuItem key={`${card.id}-${i}`} value={card.id}>
                            {card.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {hasVendors && (
              <Grid item xs={12} sx={filterFormGridSx}>
                <FormControl variant="standard" sx={[filterFormItemSx, filterFormControlPaddingSx]}>
                  <FormLabel htmlFor="Standard-scorecard-simple" sx={filterFormLabelSx}>
                    Company
                  </FormLabel>
                  <Select value={get(client, 'id', 0)} onChange={handleVendorChange}>
                    <MenuItem value={0}>
                      <em>Any</em>
                    </MenuItem>
                    {get(vendors, 'data', [])
                      .sort((a, b) => sortAlphaNum(a, b, 'vendor.name'))
                      .map((vend, i) => (
                        <MenuItem key={vend.id} value={vend.id}>
                          {get(vend, [prefixToUse, 'name'], '')}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </form>
      </DiagContainer>
    </Box>
  );
}
